import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api.js';
import {
    Text,
    Heading,
    Badge,
    Button,
    Container,
    NavLink
} from './ui/index.jsx';

/**
 * 🔒 SECURITY: HTML escape helper to prevent XSS attacks
 * Escapes HTML special characters in user-controllable content
 */
const escapeHtml = (text) => {
    if (typeof text !== 'string') return '';
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
};

/**
 * App Flow Diagram React Component - Mermaid.js Implementation
 * Converted from vanilla JS to React + Vite - COMPLETE with all original features
 * Fast, lightweight, and responsive flow diagrams using Mermaid.js
 */
const AppFlowDiagram = ({ analysisData, diagramData = null, onError }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mermaidDiagrams, setMermaidDiagrams] = useState(null);
    const [activeTab, setActiveTab] = useState('userJourney');
    const [mermaidLoaded, setMermaidLoaded] = useState(false);
    const [copiedButtons, setCopiedButtons] = useState(new Set());
    const [isGenerating, setIsGenerating] = useState(false);
    const hasGeneratedRef = useRef(false);
    const hasInitializedMermaidRef = useRef(false);
    const containerRef = useRef(null);

    // Diagram types configuration - matches backend output format exactly
    const diagramTypes = [
        { key: 'userJourney', displayKey: 'userJourney', title: 'User Journey' },
        { key: 'systemFlow', displayKey: 'systemFlow', title: 'System Flow' },
        { key: 'systemArchitecture', displayKey: 'systemArchitecture', title: 'System Architecture' },
        { key: 'authFlow', displayKey: 'authFlow', title: 'Authentication' },
        { key: 'deploymentPipeline', displayKey: 'deploymentPipeline', title: 'Deployment Pipeline' }
    ];

    // Load Mermaid.js library from CDN
    useEffect(() => {
        const loadMermaid = async () => {
            if (window.mermaid || mermaidLoaded) {
                setMermaidLoaded(true);
                return;
            }

            try {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js';

                const loadPromise = new Promise((resolve, reject) => {
                    script.onload = () => {
                        // Initialize Mermaid with responsive configuration
                        window.mermaid.initialize({
                            startOnLoad: true,
                            theme: 'dark',
                            flowchart: {
                                useMaxWidth: true,
                                htmlLabels: true,
                                curve: 'linear'
                            },
                            sequence: {
                                useMaxWidth: true,
                                wrap: true
                            },
                            journey: {
                                useMaxWidth: true
                            }
                        });
                        resolve();
                    };
                    script.onerror = () => {
                        reject(new Error('Failed to load Mermaid.js library'));
                    };
                });

                document.head.appendChild(script);
                await loadPromise;
                setMermaidLoaded(true);
            } catch (error) {
                setError(`Failed to load Mermaid.js: ${error.message}`);
                if (onError) onError(error);
            }
        };

        loadMermaid();
    }, [onError]);

    // Load existing diagrams or generate new ones when analysis data is available
    useEffect(() => {
        if (!mermaidLoaded) return;

        let parsedDiagramData = diagramData;

        // Handle string data from legacy sessions or edge cases
        if (typeof diagramData === 'string' && diagramData.length > 0) {
            try {
                parsedDiagramData = JSON.parse(diagramData);
            } catch (error) {
                parsedDiagramData = null;
            }
        }

        const storedParsedData = parsedDiagramData && typeof parsedDiagramData === 'object' && !Array.isArray(parsedDiagramData) ? parsedDiagramData : null;

        if (parsedDiagramData && (Object.prototype.toString.call(parsedDiagramData) === '[object Object]' || parsedDiagramData.userJourney)) {
            let transformedData = { ...parsedDiagramData };

            if (parsedDiagramData.errorHandling && !parsedDiagramData.deploymentPipeline) {
                transformedData = {
                    ...parsedDiagramData,
                    systemArchitecture: parsedDiagramData.systemArchitecture || parsedDiagramData.systemFlow,
                    deploymentPipeline: parsedDiagramData.errorHandling
                };
            }

            setTimeout(() => {
                setMermaidDiagrams(transformedData);
                hasGeneratedRef.current = true; // ✅ CRITICAL: Mark as generated to prevent regeneration
                setIsLoading(false);
            }, 500);
            return;
        }

        if (diagramData !== null && diagramData !== undefined) {

            if (storedParsedData) {
                parsedDiagramData = storedParsedData;
            } else {
                parsedDiagramData = null;
            }

            const expectedFields = ['userJourney', 'systemFlow', 'systemArchitecture', 'authFlow', 'deploymentPipeline'];
            const legacyFields = ['USER_JOURNEY', 'SYSTEM_ARCHITECTURE', 'AUTH_FLOW', 'ERROR_HANDLING', 'errorHandling', 'systemFlow'];

            const hasValidDiagrams = parsedDiagramData && (
                expectedFields.some(field => parsedDiagramData[field] && typeof parsedDiagramData[field] === 'string') ||
                legacyFields.some(field => parsedDiagramData[field] && typeof parsedDiagramData[field] === 'string')
            );

            if (hasValidDiagrams) {
                let transformedData = { ...parsedDiagramData };

                if (parsedDiagramData.errorHandling && !parsedDiagramData.deploymentPipeline) {
                    transformedData = {
                        ...parsedDiagramData,
                        systemArchitecture: parsedDiagramData.systemArchitecture || parsedDiagramData.systemFlow,
                        deploymentPipeline: parsedDiagramData.errorHandling
                    };
                }

                setMermaidDiagrams(transformedData);
            } else {
                setMermaidDiagrams({});
            }

            setIsLoading(false);
            hasGeneratedRef.current = true; // Prevent future generations
            return;
        }

        if (mermaidDiagrams && typeof mermaidDiagrams === 'object' && Object.keys(mermaidDiagrams).length > 0) {
            setIsLoading(false);
            hasGeneratedRef.current = true;
            return;
        }

        // ✅ CRITICAL FIX: Prevent regeneration if diagrams already exist
        if (!analysisData || isGenerating || hasGeneratedRef.current) {
            setIsLoading(false);
            return;
        }

        // Check if valid diagram data already exists (from any source)
        if (diagramData !== null || (mermaidDiagrams && Object.keys(mermaidDiagrams).length > 0)) {
            setIsLoading(false);
            hasGeneratedRef.current = true; // Mark as generated to prevent future regeneration
            return;
        }

        const generateDiagrams = async () => {
            try {
                setIsGenerating(true);
                setIsLoading(true);
                setError(null);

                const diagrams = await generateMermaidDiagrams(analysisData);

                // Ensure we have a proper object structure
                let processedDiagrams = diagrams;
                if (typeof diagrams === 'string') {
                    try {
                        processedDiagrams = JSON.parse(diagrams);
                    } catch (e) {
                        throw new Error('Invalid diagram data format');
                    }
                }

                if (processedDiagrams && typeof processedDiagrams === 'object' && !Array.isArray(processedDiagrams) && Object.keys(processedDiagrams).length > 0) {
                    setMermaidDiagrams(processedDiagrams);
                    hasGeneratedRef.current = true;
                } else {
                    throw new Error('Failed to generate flow diagrams');
                }
            } catch (error) {
                setError(error.message);
                if (onError) onError(error);
                hasGeneratedRef.current = false;
            } finally {
                setIsLoading(false);
                setIsGenerating(false);
            }
        };

        generateDiagrams();
    }, [analysisData, mermaidLoaded, diagramData]); // Removed isGenerating and mermaidDiagrams from dependencies to prevent loops

    // Initialize Mermaid diagrams after rendering
    useEffect(() => {
        if (!mermaidDiagrams || Object.keys(mermaidDiagrams).length === 0 || !mermaidLoaded || !window.mermaid) return;
        if (hasInitializedMermaidRef.current) return;

        const initializeMermaid = async () => {
            try {
                let isMounted = true;

                // Wait for container to be available with exponential backoff
                let attempts = 0;
                const maxAttempts = 20; // Increased from 10
                while (!containerRef.current && attempts < maxAttempts) {
                    const waitTime = Math.min(100 * Math.pow(1.5, attempts), 1000);
                    await new Promise(resolve => setTimeout(resolve, waitTime));
                    if (!isMounted) return;
                    attempts++;
                }

                if (!containerRef.current) {
                    setError('Mermaid container initialization failed - please try refreshing the page');
                    return;
                }

                // Clear any existing rendered diagrams
                containerRef.current.querySelectorAll('.mermaid svg').forEach(svg => svg.remove());

                // Re-initialize Mermaid with dark theme configuration
                await window.mermaid.initialize({
                    startOnLoad: false,
                    theme: 'dark',
                    securityLevel: 'loose',
                    fontFamily: 'inherit',
                    themeVariables: {
                        darkMode: true,
                        primaryColor: '#7B61FF',
                        primaryTextColor: '#E5E7EB',
                        primaryBorderColor: '#3A3D46',
                        lineColor: '#7B61FF',
                        secondaryColor: '#141722',
                        tertiaryColor: '#0F1117',
                        background: '#0F1117',
                        mainBkg: '#141722',
                        secondBkg: '#0F1117',
                        tertiaryTextColor: '#E5E7EB'
                    },
                    flowchart: {
                        useMaxWidth: true,
                        htmlLabels: true,
                        curve: 'linear'
                    },
                    sequence: {
                        useMaxWidth: true,
                        wrap: true,
                        width: 150,
                        height: 65
                    },
                    journey: {
                        useMaxWidth: true,
                        rightAngles: false
                    }
                });

                const mermaidElements = containerRef.current.querySelectorAll('.mermaid');

                if (mermaidElements.length === 0) {
                    setTimeout(() => {
                        const retryElements = containerRef.current.querySelectorAll('.mermaid');
                        if (retryElements.length > 0) {
                            setTimeout(() => initializeMermaid(), 100);
                        }
                    }, 200);
                    return;
                }

                for (let i = 0; i < mermaidElements.length; i++) {
                    const element = mermaidElements[i];
                    const id = element.id;
                    const code = element.textContent?.trim();

                    if (code) {
                        try {
                            // Clear the element first
                            element.innerHTML = '';

                            // Render the diagram with unique ID
                            const diagramId = `diagram-${id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                            const { svg } = await window.mermaid.render(diagramId, code);
                            element.innerHTML = svg;
                        } catch (renderError) {
                            // 🔒 SECURITY: Escape user-controllable content to prevent XSS
                            element.innerHTML = `
                                <div class="text-red-400 p-4 border border-red-600 rounded bg-red-900/20">
                                    <h4 class="font-semibold">Diagram Rendering Error</h4>
                                    <p class="text-sm mt-1">${escapeHtml(renderError.message)}</p>
                                    <details class="mt-2">
                                        <summary class="cursor-pointer text-xs">Show Mermaid Code</summary>
                                        <pre class="text-xs mt-1 p-2 bg-gray-800 rounded overflow-auto">${escapeHtml(code)}</pre>
                                    </details>
                                </div>
                            `;
                        }
                    }
                }

                hasInitializedMermaidRef.current = true;

            } catch (error) {
                setError(`Mermaid initialization error: ${error.message}`);
            }
        };

        // Delay to ensure DOM is fully rendered
        const timeoutId = setTimeout(initializeMermaid, 200);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [mermaidDiagrams, mermaidLoaded]);

    // Generate Mermaid diagrams by calling Claude API (RESTORED ORIGINAL FUNCTIONALITY)
    const generateMermaidDiagrams = async (analysisData) => {
        try {
            // Check for debug mode (ORIGINAL FEATURE)
            if (window.isDebugMode && window.isDebugMode()) {
                return await handleDebugMode(analysisData);
            }

            // Call the real API method
            return await generateMermaidDiagramsReal(analysisData);

        } catch (error) {
            throw error;
        }
    };

    const handleDebugMode = async (analysisData) => {

        // Create the prompt that would be sent to Claude
        const prompt = createMermaidPrompt(analysisData);

        // Show debug input and wait for user response in development
        return new Promise((resolve) => {
            if (import.meta.env.DEV && window.showDebugInput) {
                window.debugStepCallbacks = window.debugStepCallbacks || {};
                window.debugStepCallbacks['flowdiagram'] = (mockData) => {
                    if (mockData) {
                        try {
                            const parsedMockData = typeof mockData === 'string' ? JSON.parse(mockData) : mockData;
                            resolve(parsedMockData);
                        } catch (error) {
                            resolve(getFallbackDiagrams());
                        }
                    } else {
                        generateMermaidDiagramsReal(analysisData).then(resolve);
                    }
                };

                const wasShown = window.showDebugInput('flowdiagram', prompt);
                if (!wasShown) {
                    generateMermaidDiagramsReal(analysisData).then(resolve);
                }
            } else {
                generateMermaidDiagramsReal(analysisData).then(resolve);
            }
        });
    };

    const pollForDiagramCompletion = async (taskId, agentId, maxAttempts = 30) => {

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const statusResult = await api.request(`/api/v2/flow-diagram/status?taskId=${taskId}&agentId=${agentId}`);

                if (statusResult.status === 'completed' && statusResult.data) {
                    return statusResult.data;
                } else if (statusResult.status === 'failed') {
                    throw new Error(statusResult.error || 'Diagram generation failed');
                } else if (statusResult.status === 'in_progress') {
                    await new Promise(resolve => setTimeout(resolve, 2000));
                    continue;
                } else {
                    await new Promise(resolve => setTimeout(resolve, 2000));
                }
            } catch (error) {
                if (attempt === maxAttempts) {
                    throw new Error(`Polling failed after ${maxAttempts} attempts: ${error.message}`);
                }
                await new Promise(resolve => setTimeout(resolve, 2000));
            }
        }

        throw new Error('Polling timeout - diagram generation took too long');
    };

    const generateMermaidDiagramsReal = async (analysisData) => {
        try {

            // Get userId and sessionId for credit tracking
            const userId = api.getCurrentUserId();
            const sessionId = window.getSessionId ? window.getSessionId() : api.getCurrentSessionId() || 'default';

            const result = await api.generateFlowDiagramWithUserId(analysisData, sessionId, userId, false);

            if (result.status === 'started' || result.status === 'in_progress') {
                return await pollForDiagramCompletion(result.taskId, result.agentId);
            }

            if (result.mermaidDiagrams) {
                return result.mermaidDiagrams;
            }

            // Fallback error
            throw new Error('No diagram data returned from API');

        } catch (error) {
            throw error;
        }
    };


    // Create the prompt for Claude to generate Mermaid diagrams (ORIGINAL LOGIC)
    const createMermaidPrompt = (analysisData) => {
        return `Create multiple Mermaid.js diagrams for this application analysis. Generate ONLY the Mermaid syntax - no explanations, no markdown blocks.

PROJECT ANALYSIS:
${JSON.stringify(analysisData, null, 2)}

Generate these 4 types of Mermaid diagrams:
1. USER_JOURNEY - Main user flow through the application
2. SYSTEM_FLOW - Core system processes and data flow  
3. AUTH_FLOW - Authentication and security flows
4. ERROR_HANDLING - Error states and recovery flows

Return ONLY valid JSON with Mermaid syntax:
{
  "userJourney": "journey\\n    title User Journey\\n    section Registration\\n        User opens app: 5: Me",
  "systemFlow": "flowchart TD\\n    A[🎯 User Input] --> B{📊 Validate Data}",
  "authFlow": "sequenceDiagram\\n    participant U as 👤 User\\n    participant A as 🔐 Auth Service",
  "errorHandling": "flowchart LR\\n    A[⚠️ Error Occurs] --> B{Error Type?}"
}`;
    };

    // Get fallback diagrams if generation fails (ORIGINAL FUNCTIONALITY)
    const getFallbackDiagrams = () => {
        return {
            systemFlow: "flowchart TD\n    A[🎯 Application Start] --> B[📊 Process Data]\n    B --> C[✅ Display Results]",
            userJourney: "journey\n    title User Journey\n    section Start\n        Open app: 5: User\n        Navigate: 4: User\n        Use features: 5: User"
        };
    };

    // Handle tab switching
    const handleTabSwitch = (tabKey) => {
        setActiveTab(tabKey);
    };

    // Handle fullscreen functionality (FIXED)
    const handleFullscreen = (diagramKey) => {
        // Find the diagram container first
        const diagramContainer = containerRef.current?.querySelector(`#${diagramKey}`);
        if (!diagramContainer) {
            return;
        }

        // Look for the mermaid element or SVG within the container
        const mermaidElement = diagramContainer.querySelector('.mermaid') ||
            diagramContainer.querySelector('svg') ||
            diagramContainer.querySelector('.border.border-\\[\\#3A3D46\\]');

        if (mermaidElement) {
            try {
                if (mermaidElement.requestFullscreen) {
                    mermaidElement.requestFullscreen();
                } else if (mermaidElement.webkitRequestFullscreen) {
                    mermaidElement.webkitRequestFullscreen();
                } else if (mermaidElement.msRequestFullscreen) {
                    mermaidElement.msRequestFullscreen();
                } else if (mermaidElement.mozRequestFullScreen) {
                    mermaidElement.mozRequestFullScreen();
                } else {
                    // Fallback: open in new window
                    const newWindow = window.open('', '_blank', 'width=1200,height=800');
                    if (newWindow) {
                        newWindow.document.write(`
                            <!DOCTYPE html>
                            <html>
                            <head>
                                <title>${diagramTypes.find(t => t.key === diagramKey)?.title || 'Diagram'}</title>
                                <style>
                                    body { margin: 0; padding: 20px; background: #0F1117; color: white; font-family: system-ui; }
                                    .diagram-container { width: 100%; height: 90vh; overflow: auto; }
                                </style>
                            </head>
                            <body>
                                <h1>${diagramTypes.find(t => t.key === diagramKey)?.title || 'Diagram'}</h1>
                                <div class="diagram-container">${mermaidElement.outerHTML}</div>
                            </body>
                            </html>
                        `);
                        newWindow.document.close();
                    }
                }
            } catch (error) {
                // Error entering fullscreen
            }
        } else {
            // Mermaid element not found in diagram container
        }
    };

    // Handle copy code functionality (ORIGINAL FEATURE)
    const handleCopyCode = async (diagramKey, code) => {
        try {
            await navigator.clipboard.writeText(code);
            setCopiedButtons(prev => new Set([...prev, diagramKey]));

            // Remove copied state after 2 seconds
            setTimeout(() => {
                setCopiedButtons(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(diagramKey);
                    return newSet;
                });
            }, 2000);
        } catch (error) {
            // Failed to copy code
        }
    };


    // Loading state
    if (isLoading) {
        return (
            <div className="text-center py-12">
                <div className="animate-spin inline-block w-8 h-8 border-4 border-[#7B61FF] border-t-transparent rounded-full mb-4"></div>
                <Heading level={3} className="text-xl mb-2">Generating Flow Diagrams</Heading>
                <Text variant="secondary">Creating interactive Mermaid diagrams for your application...</Text>
                <div className="mt-4">
                    <Text variant="muted" size="sm" className="flex items-center justify-center">
                        Much faster with Mermaid.js (15-30 seconds)
                    </Text>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="glass-panel p-8 text-center rounded-2xl border-red-500/20">
                <Heading level={3} className="text-xl mb-2 text-white">Flow Diagram Generation Failed</Heading>
                <Text variant="secondary" className="mb-6 text-gray-300">{error}</Text>
            </div>
        );
    }

    // No diagrams available or all diagrams are invalid
    if (!mermaidDiagrams || Object.keys(mermaidDiagrams).length === 0) {
        return (
            <div className="glass-panel p-8 text-center rounded-2xl">
                <Heading level={3} className="text-xl mb-2 text-white">No Flow Diagrams Available</Heading>
                <Text variant="secondary" className="mb-4 text-gray-300">
                    The diagram data is missing or invalid. This could be due to:
                </Text>
                <ul className="text-left max-w-md mx-auto text-gray-400 space-y-2 text-sm">
                    <li>• Model returned empty or invalid diagram data</li>
                    <li>• Required diagram types (userJourney, systemFlow, etc.) are missing</li>
                    <li>• Mermaid syntax validation failed</li>
                </ul>
                <Text variant="muted" size="sm" className="mt-4 text-gray-500">
                    Please regenerate the analysis to create flow diagrams.
                </Text>
            </div>
        );
    }

    // Validate that at least one diagram is valid
    const validDiagrams = diagramTypes.filter(type => {
        const code = mermaidDiagrams[type.key];
        return code && typeof code === 'string' && code.length >= 50 && (
            code.includes('journey') ||
            code.includes('flowchart') ||
            code.includes('graph') ||
            code.includes('sequenceDiagram')
        );
    });

    if (validDiagrams.length === 0) {
        return (
            <div className="p-8 text-center border border-yellow-500/30 rounded-lg bg-yellow-500/10">
                <Heading level={3} className="text-xl mb-2 text-yellow-500">Invalid Diagram Data</Heading>
                <Text variant="secondary" className="mb-4 text-yellow-500/90">
                    All diagram types failed validation. Common issues:
                </Text>
                <ul className="text-left max-w-md mx-auto text-yellow-500/80 space-y-2 text-sm">
                    <li>• Diagrams are too short (minimum 50 characters required)</li>
                    <li>• Missing Mermaid syntax keywords (journey, flowchart, graph, sequenceDiagram)</li>
                    <li>• Diagram data is not a string (got object or other type)</li>
                </ul>
                <Text variant="muted" size="sm" className="mt-4 text-yellow-500/70">
                    Check console for detailed validation warnings.
                </Text>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="glass-panel rounded-2xl overflow-hidden relative group">
            {/* Ambient Shine Effect */}
            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{ transform: 'rotate(45deg) translate(-50%, -50%) scale(2)' }} />
            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-white/5 bg-black/20">
                <Heading level={2} className="text-xl sm:text-2xl mb-2 text-white">Interactive App Flow Diagrams</Heading>
                <Text variant="secondary" size="sm" className="sm:text-base text-gray-300">Explore your application's workflows and user journeys</Text>
            </div>

            {/* Mermaid dark theme overrides for readability */}
            <style>{`
                .mermaid svg { 
                    color: #E5E7EB !important; 
                    background: transparent !important;
                    font-family: inherit !important;
                }
                .mermaid .label, 
                .mermaid text,
                .mermaid .nodeLabel,
                .mermaid .edgeLabel { 
                    fill: #E5E7EB !important; 
                    color: #E5E7EB !important;
                    font-size: 14px !important;
                }
                .mermaid .node rect, 
                .mermaid .node circle, 
                .mermaid .node path,
                .mermaid .flowchart-node { 
                    fill: #141722 !important; 
                    stroke: #7B61FF !important; 
                    stroke-width: 2px !important;
                }
                .mermaid .cluster rect { 
                    fill: #0F1117 !important; 
                    stroke: #3A3D46 !important; 
                }
                .mermaid .edgePath path,
                .mermaid .flowchart-link { 
                    stroke: #7B61FF !important; 
                    stroke-width: 2px !important;
                }
                .mermaid .journey-section { 
                    fill: #141722 !important; 
                }
                .mermaid .actor { 
                    fill: #141722 !important; 
                    stroke: #7B61FF !important; 
                }
                .mermaid .messageLine0,
                .mermaid .messageLine1 { 
                    stroke: #7B61FF !important; 
                }
                .mermaid .labelBox { 
                    fill: #141722 !important; 
                    stroke: #3A3D46 !important; 
                }
                .mermaid .loopText,
                .mermaid .noteText { 
                    fill: #E5E7EB !important; 
                }
            `}</style>

            {/* Tabs Navigation - Mobile Responsive */}
            <div className="bg-black/20 px-4 sm:px-8 py-4 border-b border-white/5">
                {/* Mobile: Dropdown/Select Style Navigation */}
                <div className="block md:hidden">
                    <div className="relative">
                        <select
                            value={activeTab}
                            onChange={(e) => handleTabSwitch(e.target.value)}
                            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 pr-10 text-white font-medium text-sm focus:outline-none focus:ring-2 focus:ring-[#7B61FF] focus:border-[#7B61FF] appearance-none"
                        >
                            {diagramTypes.map((type) => (
                                <option key={type.displayKey} value={type.displayKey} className="bg-black text-white">
                                    {type.title}
                                </option>
                            ))}
                        </select>
                        {/* Custom dropdown arrow */}
                        <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Desktop: Button Navigation */}
                <nav className="hidden md:flex justify-center items-center space-x-3">
                    {diagramTypes.map((type) => (
                        <button
                            key={type.displayKey}
                            onClick={() => handleTabSwitch(type.displayKey)}
                            className={`
                                relative px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 
                                whitespace-nowrap flex items-center justify-center min-w-[120px] flex-shrink-0
                                ${activeTab === type.displayKey
                                    ? 'bg-[#7B61FF] text-white shadow-[0_0_15px_rgba(123,97,255,0.4)] transform scale-105'
                                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/5'
                                }
                            `}
                        >
                            {activeTab === type.displayKey && (
                                <div className="absolute inset-0 bg-gradient-to-r from-[#7B61FF] to-[#9333EA] rounded-xl opacity-50 blur-sm -z-10"></div>
                            )}
                            <span className="relative z-10">{type.title}</span>
                        </button>
                    ))}
                </nav>
            </div>

            {/* Diagram Contents */}
            <div className="diagram-contents">
                {diagramTypes.map((type) => {
                    const diagramCode = mermaidDiagrams[type.key];

                    // Comprehensive validation of diagram code
                    if (!diagramCode || typeof diagramCode !== 'string' || diagramCode.length < 50) {
                        return null;
                    }

                    // Validate Mermaid syntax keywords are present
                    const hasMermaidSyntax =
                        diagramCode.includes('journey') ||
                        diagramCode.includes('flowchart') ||
                        diagramCode.includes('graph') ||
                        diagramCode.includes('sequenceDiagram');

                    if (!hasMermaidSyntax) {
                        return null;
                    }

                    const diagramId = `mermaid-${type.displayKey}-${Date.now()}`;
                    const isActive = activeTab === type.displayKey;
                    const isCopied = copiedButtons.has(type.displayKey);

                    return (
                        <div
                            key={type.displayKey}
                            id={type.displayKey}
                            className={`diagram-content p-4 sm:p-6 ${!isActive ? 'hidden' : ''}`}
                            style={{ display: isActive ? 'block' : 'none' }}
                        >
                            <div className="mb-4">
                                <Heading level={3} className="text-lg mb-2">{type.title}</Heading>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <Button
                                        onClick={() => handleFullscreen(type.displayKey)}
                                        variant="secondary"
                                        size="sm"
                                        className="flex items-center bg-white/5 hover:bg-white/10 border-white/10 text-gray-300 hover:text-white"
                                    >
                                        Fullscreen
                                    </Button>
                                    <Button
                                        onClick={() => handleCopyCode(type.displayKey, diagramCode)}
                                        variant={isCopied ? "primary" : "secondary"}
                                        size="sm"
                                        className={`flex items-center ${isCopied ? 'bg-[#7B61FF] text-white' : 'bg-white/5 hover:bg-white/10 border-white/10 text-gray-300 hover:text-white'}`}
                                    >
                                        {isCopied ? 'Copied!' : 'Copy Code'}
                                    </Button>
                                </div>
                            </div>
                            <div className="border border-white/10 rounded-xl p-4 bg-black/40 overflow-auto" style={{ minHeight: '420px' }} data-mermaid-diagram={type.displayKey}>
                                <div id={diagramId} className="mermaid">
                                    {diagramCode}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Export the component
export default AppFlowDiagram; 